<template>
  <section class="accruals-estimation-rule-panel" data-testid="accruals-configuration-page__rule-panel">
    <div class="accruals-estimation-rule-panel__scroll">
      <header class="accruals-estimation-rule-panel__section-header">
        <h2 class="accruals-estimation-rule-panel__section-title">Rule Details</h2>
        <div class="accruals-estimation-rule-panel__active-toggle">
          <span class="accruals-estimation-rule-panel__active-label">Active</span>
          <base-switch
            :value="rule.active"
            :disabled="readOnly"
            aria-label="Rule active"
            data-testid="accruals-configuration-page__rule-active-toggle"
            @input="emitPatch({ active: $event })"
          />
        </div>
      </header>

      <div class="accruals-estimation-rule-panel__details-card">
        <div class="accruals-estimation-rule-panel__row accruals-estimation-rule-panel__row--split">
          <div class="accruals-estimation-rule-panel__field accruals-estimation-rule-panel__field--grow">
            <label class="accruals-estimation-rule-panel__label">Rule Name</label>
            <lb-string
              :value="rule.name"
              hidedetails
              label=""
              class="accruals-estimation-rule-panel__input"
              data-testid="accruals-configuration-page__rule-name"
              @input="emitPatch({ name: $event })"
            />
          </div>
          <div class="accruals-estimation-rule-panel__field accruals-estimation-rule-panel__field--apply-on">
            <label class="accruals-estimation-rule-panel__label">Apply On</label>
            <div class="accruals-estimation-rule-panel__segmented" data-testid="accruals-configuration-page__apply-on">
              <button
                type="button"
                class="accruals-estimation-rule-panel__segment"
                :class="{ 'accruals-estimation-rule-panel__segment--active': rule.applyOn === applyOnVendor }"
                @click="onApplyOnChange(applyOnVendor)"
              >
                Vendor
              </button>
              <button
                type="button"
                class="accruals-estimation-rule-panel__segment"
                :class="{ 'accruals-estimation-rule-panel__segment--active': rule.applyOn === applyOnPo }"
                @click="onApplyOnChange(applyOnPo)"
              >
                PO
              </button>
            </div>
          </div>
        </div>

        <div class="accruals-estimation-rule-panel__row accruals-estimation-rule-panel__row--split">
          <div class="accruals-estimation-rule-panel__field">
            <label class="accruals-estimation-rule-panel__label">Effective From</label>
            <lb-date
              :value="rule.effectiveFrom"
              hidedetails
              label=""
              menu
              :attach="false"
              clearable
              class="accruals-estimation-rule-panel__input"
              data-testid="accruals-configuration-page__effective-from"
              @input="emitPatch({ effectiveFrom: $event })"
            />
          </div>
          <div class="accruals-estimation-rule-panel__field">
            <label class="accruals-estimation-rule-panel__label">Effective To</label>
            <lb-date
              :value="rule.effectiveTo"
              hidedetails
              label=""
              menu
              :attach="false"
              :min="rule.effectiveFrom || undefined"
              clearable
              class="accruals-estimation-rule-panel__input"
              data-testid="accruals-configuration-page__effective-to"
              @input="emitPatch({ effectiveTo: $event })"
            />
          </div>
        </div>

        <div
          v-if="rule.applyOn === applyOnVendor"
          class="accruals-estimation-rule-panel__field"
        >
          <label class="accruals-estimation-rule-panel__label">Select Vendors</label>
          <accruals-estimation-vendor-select
            :value="rule.vendorIds"
            @input="emitPatch({ vendorIds: $event })"
          />
        </div>
      </div>

      <section
        class="accruals-estimation-rule-panel__filter"
        data-testid="accruals-configuration-page__filter-criteria"
      >
        <header class="accruals-estimation-rule-panel__section-header">
          <div class="accruals-estimation-rule-panel__heading">
            <h2 class="accruals-estimation-rule-panel__section-title">{{ filterTitle }}</h2>
            <p class="accruals-estimation-rule-panel__section-subtitle">{{ filterSubtitle }}</p>
          </div>
        </header>
        <div class="accruals-estimation-rule-panel__conditions-card">
          <accruals-im-groups-builder
            embedded
            :editing="!readOnly"
            :groups="rule.groups || []"
            :group-logic="rule.groupLogic"
            :group-connectors="rule.groupConnectors || []"
            :saving="saving"
            :condition-errors="conditionErrors"
            :field-items-override="conditionFields"
            @add-group="$emit('add-group')"
            @remove-group="$emit('remove-group', $event)"
            @set-group-logic="$emit('set-group-logic', $event)"
            @set-group-connector="$emit('set-group-connector', $event)"
            @add-condition="$emit('add-condition', $event)"
            @update-condition="$emit('update-condition', $event)"
            @delete-condition="$emit('delete-condition', $event)"
          />
        </div>
      </section>

      <accruals-estimation-method-section
        :apply-on="rule.applyOn"
        :selected-method-id="rule.estimationMethod"
        :method-config="rule.methodConfig"
        :templates="templates"
        @select-method="onSelectMethod"
      />

      <section class="accruals-estimation-rule-panel__gl-section">
        <header class="accruals-estimation-rule-panel__section-header">
          <div class="accruals-estimation-rule-panel__heading">
            <h2 class="accruals-estimation-rule-panel__section-title">Default GL Codes</h2>
            <p class="accruals-estimation-rule-panel__section-subtitle">
              Prefill account assignment in estimate workbench
            </p>
          </div>
          <base-switch
            :value="rule.defaultGlEnabled"
            aria-label="Default GL codes"
            data-testid="accruals-configuration-page__default-gl-toggle"
            @input="emitPatch({ defaultGlEnabled: $event })"
          />
        </header>

        <div
          v-if="rule.defaultGlEnabled"
          class="accruals-estimation-rule-panel__details-card"
        >
          <div class="accruals-estimation-rule-panel__row accruals-estimation-rule-panel__row--split">
            <div class="accruals-estimation-rule-panel__field">
              <label class="accruals-estimation-rule-panel__label">Debit GL</label>
              <lb-dropdown
                :value="rule.debitGl"
                :items="glDropdownItems"
                hidedetails
                label=""
                searchable
                placeholder="Select GL..."
                class="accruals-estimation-rule-panel__input"
                data-testid="accruals-configuration-page__debit-gl"
                @input="emitPatch({ debitGl: $event })"
              />
            </div>
            <div class="accruals-estimation-rule-panel__field">
              <label class="accruals-estimation-rule-panel__label">Credit GL</label>
              <lb-dropdown
                :value="rule.creditGl"
                :items="glDropdownItems"
                hidedetails
                label=""
                searchable
                placeholder="Select GL..."
                class="accruals-estimation-rule-panel__input"
                data-testid="accruals-configuration-page__credit-gl"
                @input="emitPatch({ creditGl: $event })"
              />
            </div>
          </div>
        </div>
      </section>
    </div>

    <footer
      v-if="!readOnly"
      class="accruals-estimation-rule-panel__footer"
    >
      <base-button
        text
        plain
        reject
        label="Delete rule"
        class="accruals-estimation-rule-panel__delete-btn"
        data-testid="accruals-configuration-page__delete-rule-btn"
        @click="$emit('delete-rule')"
      />
      <div class="accruals-estimation-rule-panel__footer-actions">
        <base-button
          label="Cancel"
          secondary
          outlined
          :disabled="saving"
          data-testid="accruals-configuration-page__rule-cancel-btn"
          @click="$emit('cancel')"
        />
        <base-button
          label="Save rule"
          primary
          class="ml-2"
          :loading="saving"
          :disabled="saving"
          data-testid="accruals-configuration-page__rule-save-btn"
          @click="$emit('save')"
        />
      </div>
    </footer>
  </section>
</template>

<script>
import AccrualsImGroupsBuilder from './AccrualsImGroupsBuilder.vue';
import AccrualsEstimationVendorSelect from './AccrualsEstimationVendorSelect.vue';
import AccrualsEstimationMethodSection from './AccrualsEstimationMethodSection.vue';
import {
  ESTIMATION_APPLY_ON,
  ESTIMATION_AP_CONDITION_FIELDS,
  ESTIMATION_METHODS_BY_APPLY_ON,
  ESTIMATION_FILTER_CRITERIA_TITLE,
  ESTIMATION_FILTER_CRITERIA_SUBTITLE,
  catalogTemplatesToMethods,
  createEmptyEstimationRule,
} from '../accrualsEstimationRulesConstants';
import { OPEN_PO_CONDITION_FIELDS } from '../accrualsConfigurationConstants';

export default {
  name: 'AccrualsEstimationRulePanel',
  components: {
    AccrualsImGroupsBuilder,
    AccrualsEstimationVendorSelect,
    AccrualsEstimationMethodSection,
  },
  props: {
    rule: { type: Object, required: true },
    saving: { type: Boolean, default: false },
    conditionErrors: { type: Object, default: () => ({}) },
    readOnly: { type: Boolean, default: false },
    templates: { type: Array, default: () => [] },
  },
  data() {
    return {
      applyOnVendor: ESTIMATION_APPLY_ON.VENDOR,
      applyOnPo: ESTIMATION_APPLY_ON.PO,
      filterTitle: ESTIMATION_FILTER_CRITERIA_TITLE,
      filterSubtitle: ESTIMATION_FILTER_CRITERIA_SUBTITLE,
    };
  },
  computed: {
    storeGlOptions() {
      return this.$store.state.accruals?.glOptions
        || this.$store.state.m_financialclose?.accruals?.glOptions
        || [];
    },
    glDropdownItems() {
      const options = [...this.storeGlOptions];
      [this.rule?.debitGl, this.rule?.creditGl].forEach((value) => {
        if (value && !options.includes(value)) options.push(value);
      });
      return options;
    },
    conditionFields() {
      return this.rule.applyOn === ESTIMATION_APPLY_ON.PO
        ? OPEN_PO_CONDITION_FIELDS
        : ESTIMATION_AP_CONDITION_FIELDS;
    },
  },
  created() {
    this.$store.dispatch('accruals/loadGlOptions');
  },
  methods: {
    emitPatch(patch) {
      if (this.readOnly) return;
      this.$emit('update-rule', patch);
    },
    onApplyOnChange(applyOn) {
      if (applyOn === this.rule.applyOn) return;
      const catalogMethods = catalogTemplatesToMethods(this.templates, applyOn);
      const methodIds = catalogMethods.length
        ? catalogMethods.map((method) => method.id)
        : ESTIMATION_METHODS_BY_APPLY_ON[applyOn];
      const estimationMethod = methodIds[0];
      const defaults = createEmptyEstimationRule({ applyOn, estimationMethod });
      this.emitPatch({
        applyOn,
        estimationMethod,
        methodConfig: {},
        badge: catalogMethods[0]?.badge || defaults.badge,
        groups: defaults.groups,
        groupLogic: defaults.groupLogic,
        groupConnectors: defaults.groupConnectors,
        conditions: defaults.conditions,
        ruleMeta: defaults.ruleMeta,
        vendorIds: applyOn === ESTIMATION_APPLY_ON.VENDOR
          ? this.rule.vendorIds
          : defaults.vendorIds,
      });
    },
    onSelectMethod({ methodId, methodConfig, badge }) {
      this.emitPatch({
        estimationMethod: methodId,
        methodConfig: methodConfig || {},
        badge,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimationRulePanel.scss';
</style>
